<template>
  <div class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mb-6 sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Users</h1>
        <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
          A list of all users in the system including their name, email, role, and status.
        </p>
      </div>
    </div>

    <UsersFilter @search="search"/>

    <div v-if="loading" class="flex justify-center items-center py-10">
      <div class="w-10 h-10 rounded-full border-t-2 border-b-2 animate-spin border-primary"></div>
    </div>

    <div v-else-if="error" class="p-4 mb-6 bg-red-50 rounded-md dark:bg-red-900/20">
      <p class="text-red-700 dark:text-red-400">{{ error }}</p>
    </div>

    <div v-else>
      <DataTable
        :items="users"
        :columns="columns"
        :pagination="usersPagination"
        @page-change="($page) => fetchUsers($page, customFilterObject)"
        @route-change="viewUserDetails"
      >
        <template #role="{ item }">
          {{ userRoles[item.role] }}
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAdminStore } from '@/stores/adminStore';
import DataTable from '@/components/general/DataTable.vue';
import { ROUTES } from '@/router/routeNames';
import UsersFilter from '@/components/admin/UsersFilter.vue';
import { FilterObject } from '@/types';
import { userRoles } from '@/constants';

const router = useRouter();
const adminStore = useAdminStore();
const search = (filterObject: FilterObject | null) => {
  customFilterObject.value = filterObject;
  fetchUsers(1, customFilterObject.value);
}
// Get data from the store
const users = computed(() => adminStore.users);
const usersPagination = computed(() => adminStore.usersPagination);
const loading = computed(() => adminStore.loading);
const error = computed(() => adminStore.error);
const customFilterObject = ref<FilterObject | null>(null);
// Define table columns
const columns = [
  { key: 'id', label: 'ID', width: '80px' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role',
    format: (item) => item.role.charAt(0).toUpperCase() + item.role.slice(1),
    class: 'capitalize'
  },
  {
    key: 'is_deactivated',
    label: 'Account',
    format: (item: { is_deactivated?: boolean }) => (item.is_deactivated ? 'Deactivated' : 'Active'),
    class: (item: { is_deactivated?: boolean }) => (item.is_deactivated ? 'text-amber-700 dark:text-amber-400' : ''),
  },
  {
    key: 'created_at',
    label: 'Joined',
    format: (item) => new Date(item.created_at).toLocaleDateString()
  }
];

// Methods
const fetchUsers = (page = 1, filterObject: FilterObject | null = null) => {
  adminStore.fetchUsers(page, filterObject);
};

const viewUserDetails = (userId: string) => {
  router.push({name: ROUTES.ADMIN_USER, params: {id: userId}});
};

// Fetch users on component mount
onMounted(() => {
  fetchUsers();
});
</script>
